import { matchWinnerSeats, type GameStateData, type PendingMuggins } from '../game/GameState.js';
import type { Tile, Turn } from '../game/types.js';
import { tileName } from './notation.js';
import { goatLine, penaltyHandLines } from '../ui/resultText.js';
import { isPenaltyScoring } from '../ui/scoreDisplay.js';

/**
 * Translation function — same shape as `t` from `@bn-games/i18n-core`, but
 * injectable so the announcer is unit-testable without booting i18next.
 */
export type Translator = (key: string, vars?: Record<string, string | number>) => string;

/** Mode label for "you" vs "Player X" framing. Mirrors checkers + backgammon. */
export type AnnouncerMode = 'ai' | 'online' | 'local';

export interface AnnouncerContextProvider {
  readonly getMode: () => AnnouncerMode;
  /** The seat the local viewer sits on — null in hot-seat (no single local). */
  readonly getLocalSeat: () => number | null;
  /** Format a seat as a name ("Player 1", "Bot", etc.). Caller owns name resolution. */
  readonly getSeatLabel: (seat: number) => string;
}

export interface NarratedEvent {
  readonly text: string;
  /** When true, route through the assertive live region (interrupts SR). */
  readonly assertive?: boolean;
}

/**
 * Cursor target — what the focus chain currently points at. The announcer
 * formats each shape into a polite string for the live region.
 */
export type CursorTarget =
  | {
      readonly kind: 'hand';
      readonly tile: Tile;
      readonly index: number;
      readonly count: number;
      /**
       * Forced-lead state, if any. `'this'` = cursor is on the forced tile;
       * `'other'` + `forcedTile` = cursor is on a non-forced tile and the
       * forced one is elsewhere in the hand. `null` = no forced-lead constraint.
       */
      readonly forcedLead: 'this' | 'other' | null;
      readonly forcedTile: Tile | null;
    }
  | {
      readonly kind: 'armSlot';
      readonly tile: Tile;
      readonly armIndex: number;
      readonly fold: Turn;
      readonly slotIndex: number;
      readonly slotCount: number;
      /** Points this slot would score on commit (0 = no score). */
      readonly scores: number;
    }
  | { readonly kind: 'action'; readonly action: 'draw' | 'muggins' };

/**
 * Stateless formatter that turns domino events into screen-reader strings.
 * Per-app — mirrors backgammon's iter 3 announcer and checkers' iter 4,
 * because every game's move shape differs. Pulled into a third copy would
 * be the signal to extract a shared abstraction (CLAUDE.md "third copy" rule).
 */
export class MoveAnnouncer {
  private readonly t: Translator;
  private readonly provider: AnnouncerContextProvider;
  // Track whose turn was last announced so we don't re-fire after every
  // internal state copy / render tick.
  private lastAnnouncedSeat: number | null = null;

  constructor(t: Translator, provider: AnnouncerContextProvider) {
    this.t = t;
    this.provider = provider;
  }

  /** Forget the last-announced seat — call on a fresh hand / match. */
  resetTurnMemory(): void {
    this.lastAnnouncedSeat = null;
  }

  // ─── Move narration ────────────────────────────────────────────────────

  /**
   * Narrate a committed play. `wasLead === true` when this was the opening
   * tile of the hand. `openEnds` is the post-move list of active arm open
   * values (used in the "Open ends: …" tail); `points` is what was awarded
   * for the move (0 = no score; `pendingMuggins` flips into the optional
   * self-prompt for strict-Muggins claim). `scoreless` (Козёл — no scoring
   * during play) drops the "No score." tail, which would be noise there.
   */
  describePlay(
    seat: number,
    tile: Tile,
    armIndex: number,
    wasLead: boolean,
    openEnds: readonly number[],
    points: number,
    pendingMuggins: PendingMuggins | null,
    now: number,
    scoreless = false,
  ): NarratedEvent {
    const isSelf = this.isSelf(seat);
    const tileLabel = this.tileLabel(tile);
    const armLabel = this.armLabel(armIndex);
    const stem = wasLead
      ? isSelf
        ? this.t('narration.move.selfLead', { tile: tileLabel })
        : this.t('narration.move.otherLead', { subject: this.subjectFor(seat), tile: tileLabel })
      : isSelf
        ? this.t('narration.move.selfPlay', { tile: tileLabel, arm: armLabel })
        : this.t('narration.move.otherPlay', { subject: this.subjectFor(seat), tile: tileLabel, arm: armLabel });
    const endsText = openEnds.length > 0
      ? this.t('narration.move.openEnds', { ends: openEnds.join(', ') })
      : '';
    const scoreText = points > 0
      ? isSelf
        ? this.t('narration.move.scoredSelf', { points })
        : this.t('narration.move.scoredOther', { points })
      : scoreless ? '' : this.t('narration.move.noScore');
    const pieces = [stem, endsText, scoreText].filter((s) => s.length > 0);
    let text = pieces.join(' ');
    if (isSelf && pendingMuggins && pendingMuggins.scorerSeat === seat) {
      const secondsLeft = Math.max(0, Math.ceil((pendingMuggins.expiresAt - now) / 1000));
      text = `${text} ${this.t('narration.move.mugginsPromptSelf', { seconds: secondsLeft })}`;
    }
    return { text };
  }

  /** Narrate a tile draw. Boneyard composition is hidden — count only. */
  describeDraw(seat: number, handSizeAfter: number): NarratedEvent {
    if (this.isSelf(seat)) {
      return { text: this.t('narration.draw.self', { count: handSizeAfter }) };
    }
    return {
      text: this.t('narration.draw.other', {
        subject: this.subjectFor(seat),
        count: handSizeAfter,
      }),
    };
  }

  /** Narrate a pass. */
  describePass(seat: number): NarratedEvent {
    if (this.isSelf(seat)) return { text: this.t('narration.pass.self') };
    return { text: this.t('narration.pass.other', { subject: this.subjectFor(seat) }) };
  }

  /** Narrate a muggins claim. Assertive — the points changed. */
  describeMugginsClaim(seat: number, points: number): NarratedEvent {
    if (this.isSelf(seat)) {
      return {
        text: this.t('narration.muggins.claimSelf', { points }),
        assertive: true,
      };
    }
    return {
      text: this.t('narration.muggins.claimOther', {
        subject: this.subjectFor(seat),
        points,
      }),
      assertive: true,
    };
  }

  /** Narrate a forfeited muggins window. */
  describeMugginsExpired(): NarratedEvent {
    return { text: this.t('narration.muggins.expired') };
  }

  // ─── Turn / cursor / overlay ──────────────────────────────────────────

  /**
   * Narrate whose turn it is. Deduplicates against the last-announced seat
   * so repeated calls from the rAF loop don't spam the SR.
   */
  describeTurnStart(seat: number): NarratedEvent | null {
    if (this.lastAnnouncedSeat === seat) return null;
    this.lastAnnouncedSeat = seat;
    if (this.isSelf(seat)) return { text: this.t('narration.turn.you') };
    return {
      text: this.t('narration.turn.other', { subject: this.subjectFor(seat) }),
    };
  }

  /** Narrate whatever the keyboard focus chain is currently pointing at. */
  describeCursor(target: CursorTarget): NarratedEvent {
    switch (target.kind) {
      case 'hand': {
        const tileLabel = this.tileLabel(target.tile);
        if (target.forcedLead === 'this') {
          return {
            text: this.t('narration.cursor.handTileForcedLead', { tile: tileLabel }),
          };
        }
        if (target.forcedLead === 'other' && target.forcedTile !== null) {
          return {
            text: this.t('narration.cursor.handTileMustLead', {
              tile: tileLabel,
              index: target.index + 1,
              count: target.count,
              forced: this.tileLabel(target.forcedTile),
            }),
          };
        }
        return {
          text: this.t('narration.cursor.handTile', {
            tile: tileLabel,
            index: target.index + 1,
            count: target.count,
          }),
        };
      }
      case 'armSlot': {
        const tileLabel = this.tileLabel(target.tile);
        const armLabel = this.armLabel(target.armIndex);
        const foldLabel = this.foldLabel(target.fold);
        if (target.scores > 0) {
          return {
            text: this.t('narration.cursor.armSlotScores', {
              tile: tileLabel,
              arm: armLabel,
              fold: foldLabel,
              index: target.slotIndex + 1,
              count: target.slotCount,
              points: target.scores,
            }),
          };
        }
        return {
          text: this.t('narration.cursor.armSlot', {
            tile: tileLabel,
            arm: armLabel,
            fold: foldLabel,
            index: target.slotIndex + 1,
            count: target.slotCount,
          }),
        };
      }
      case 'action': {
        if (target.action === 'draw') return { text: this.t('narration.cursor.actionDraw') };
        return { text: this.t('narration.cursor.actionMuggins') };
      }
    }
  }

  // ─── Hand / match end ─────────────────────────────────────────────────

  /**
   * Narrate end-of-hand: who went out (or that the hand blocked) and what
   * each winner scored. Always assertive — it interrupts the SR so the
   * result reaches the user before the modal grabs focus.
   */
  describeHandOver(
    state: GameStateData,
  ): NarratedEvent {
    const result = state.lastResult;
    if (result === null) return { text: this.t('narration.handOver.blocked', { hand: state.handNumber }), assertive: true };
    if (isPenaltyScoring(state)) {
      const stem = this.t('narration.handOver.penaltyStem', { hand: result.handNumber });
      const lines = penaltyHandLines(state, (seat) => this.subjectFor(seat), this.t);
      return { text: [stem, ...lines].join(' '), assertive: true };
    }
    const winnerNames = result.winnerSeats.map((s) => this.provider.getSeatLabel(s)).join(', ');
    const stem = result.blocked
      ? this.t('narration.handOver.blocked', { hand: result.handNumber })
      : this.t('narration.handOver.wentOut', { hand: result.handNumber, names: winnerNames });
    const totalAward = result.winnerSeats.reduce(
      (acc, seat) => acc + (result.perSeatAward[seat] ?? 0),
      0,
    );
    if (totalAward > 0) {
      const awarded = this.t('narration.handOver.awarded', {
        names: winnerNames,
        points: totalAward,
      });
      return { text: `${stem} ${awarded}`, assertive: true };
    }
    return { text: stem, assertive: true };
  }

  /** Narrate end-of-match. Assertive — interrupts. */
  describeMatchOver(state: GameStateData): NarratedEvent {
    if (isPenaltyScoring(state)) {
      const goat = goatLine(state, (seat) => this.subjectFor(seat), this.t);
      const stem = this.t('narration.matchOver.penaltyStem');
      return { text: goat === null ? stem : `${stem} ${goat}`, assertive: true };
    }
    const winners = matchWinnerSeats(state);
    if (winners.length === 0) {
      return { text: this.t('narration.matchOver.draw'), assertive: true };
    }
    const score = Math.max(...winners.map((s) => state.scores[s] ?? 0));
    const names = winners.map((s) => this.provider.getSeatLabel(s)).join(' + ');
    const key = winners.length > 1 ? 'narration.matchOver.team' : 'narration.matchOver.win';
    return { text: this.t(key, { names, score }), assertive: true };
  }

  // ─── Helpers ──────────────────────────────────────────────────────────

  private tileLabel(tile: Tile): string {
    return tileName(tile, {
      the: this.t('narration.tile.the'),
      doublePrefix: this.t('narration.tile.doublePrefix'),
      blankLabel: this.t('narration.tile.blank'),
    });
  }

  private armLabel(armIndex: number): string {
    if (armIndex === -1) {
      // Lead has no arm; callers should be using the selfLead / otherLead
      // variants, but fall back to "first arm" so a misuse doesn't crash.
      return this.t('narration.arms.one');
    }
    const key =
      armIndex === 0
        ? 'narration.arms.one'
        : armIndex === 1
          ? 'narration.arms.two'
          : armIndex === 2
            ? 'narration.arms.three'
            : 'narration.arms.four';
    return this.t(key);
  }

  private foldLabel(turn: Turn): string {
    if (turn === 'left') return this.t('narration.folds.left');
    if (turn === 'right') return this.t('narration.folds.right');
    // 'auto' resolves at layout time; narration treats it as "straight" since
    // most auto folds settle that way. (The narrator only sees committed turns.)
    return this.t('narration.folds.straight');
  }

  private isSelf(seat: number): boolean {
    const local = this.provider.getLocalSeat();
    if (local === null) return false;
    return local === seat;
  }

  private subjectFor(seat: number): string {
    return this.provider.getSeatLabel(seat);
  }
}

/**
 * Convenience helper — derive the open-ends list a `MoveAnnouncer` consumes
 * from a post-play chain. Live state's `chain.arms[i].openValue` is the
 * authoritative source; this filter pulls only the active (scored) arms.
 *
 * `state` is the post-move state; arms not in `activeArms` (latent perpendicular
 * arms before their first play) are skipped so the narration matches the
 * scoring math.
 */
export function activeOpenEnds(state: GameStateData): readonly number[] {
  const ends: number[] = [];
  for (const armIdx of state.chain.activeArms) {
    const arm = state.chain.arms[armIdx];
    if (arm) ends.push(arm.openValue);
  }
  return ends;
}

